/**
 * CRM AuthContext — Location-Aware Login with Idle Timeout
 *
 * State machine:
 *   unauthenticated → active ⇄ session_expired
 *
 * - Full credentials + location selector at login
 * - JWT stored in localStorage. The device is SHARED: the floor's tablets pass from one
 *   salesperson to the next, so nothing about a user may outlive their logout on the device.
 *   UI preferences live on the server and follow the login (PreferencesContext, S4b).
 * - 30-minute idle timeout → session_expired
 * - force_password_change detection
 * - Location change without re-login
 */

import React, { createContext, useState, useContext, useEffect, useMemo, useCallback, useRef } from 'react';

import apiClient, { api } from '../services/apiClient';
import { authFetch } from '../services/authFetch';
import errorTracker from '../services/errorTracker';

// Exported so a component that is EMBEDDED in someone else's screen can read the session
// without useAuth(), which throws when there is no provider — a panel dropped into a drawer
// should not be able to break that drawer's own tests (R1d).
export const AuthContext = createContext(null);

/**
 * Location color map — matches spec
 */
const LOCATION_COLORS = {
  brampton: '#C8614A',
  etobicoke: '#2B8FAD',
  mississauga: '#1A2332',
  springdale: '#5A7A52'
};

function getLocationColor(name) {
  if (!name) return '#64748b';
  const key = name.toLowerCase().trim();
  for (const [loc, color] of Object.entries(LOCATION_COLORS)) {
    if (key.includes(loc)) return color;
  }
  return '#64748b';
}

// Idle timer constants
// The user's permission codes from GET /api/auth/me (F1): kept on the device so a restored
// session gates the sidebar before /me answers, and dropped at sign-out with the rest.
const PERMISSIONS_KEY = 'auth_permissions';

/** The stored codes, or [] when absent or not a list of strings. */
function readStoredPermissions() {
  try {
    const parsed = JSON.parse(localStorage.getItem(PERMISSIONS_KEY));
    return Array.isArray(parsed) && parsed.every((c) => typeof c === 'string') ? parsed : [];
  } catch {
    return [];
  }
}

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appState, setAppState] = useState('unauthenticated'); // 'unauthenticated' | 'active' | 'session_expired'
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  // Permission codes: the union of the user's RBAC and POS sets, as /me returns them (F1).
  const [permissions, setPermissions] = useState([]);

  const [authError, setAuthError] = useState(null);

  // Location state
  const [activeLocation, setActiveLocation] = useState(null);

  // Idle timer refs
  const idleTimerRef = useRef(null);
  const heartbeatRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  // ── Idle Timer ──────────────────────────────────────────────────────

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      setAppState('session_expired');
    }, IDLE_TIMEOUT_MS);
  }, []);

  const startIdleTimer = useCallback(() => {
    resetIdleTimer();

    const handler = () => resetIdleTimer();
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, handler, { passive: true });
    }

    // Heartbeat: keep session alive on server
    heartbeatRef.current = setInterval(() => {
      const currentToken = localStorage.getItem('auth_token');
      if (currentToken) {
        fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${currentToken}` }
        }).then((resp) => {
          if (resp.status === 429) {
            resp.json().then((data) => {
              const retryAfter = data.retryAfter
                ? new Date(data.retryAfter).toLocaleTimeString()
                : 'a moment';
              setAuthError(`Too many requests. Please try again after ${retryAfter}.`);
            }).catch(() => {
              setAuthError('Too many requests. Please wait a moment.');
            });
          } else if (resp.ok) {
            setAuthError(null);
            // A /me answer like any other: its codes replace the held ones (F1).
            resp.json().then((data) => {
              if (data && data.success && Array.isArray(data.data?.permissions)) {
                const codes = data.data.permissions.filter((c) => typeof c === 'string');
                setPermissions(codes);
                localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(codes));
              }
            }).catch(() => {});
          }
        }).catch(() => {});
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, handler);
      }
    };
  }, [resetIdleTimer, API_URL]);

  const stopIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
  }, []);

  // Start/stop idle timer based on appState
  useEffect(() => {
    if (appState === 'active' && !forcePasswordChange) {
      const cleanup = startIdleTimer();
      return cleanup;
    } else {
      stopIdleTimer();
    }
  }, [appState, forcePasswordChange, startIdleTimer, stopIdleTimer]);

  // ── Auth Events ─────────────────────────────────────────────────────

  useEffect(() => {
    const handleLogoutEvent = () => {
      setUser(null);
      setToken(null);
      setActiveLocation(null);
      setAppState('unauthenticated');
      setForcePasswordChange(false);
      setPermissions([]);
      localStorage.removeItem(PERMISSIONS_KEY);
    };

    window.addEventListener('auth:logout', handleLogoutEvent);
    return () => window.removeEventListener('auth:logout', handleLogoutEvent);
  }, []);

  // ── Fetch Current User ──────────────────────────────────────────────

  // keepUser: take only the permission codes from the answer (sign-in: the login response's
  // user carries fields /me does not, such as activeLocationId).
  const fetchCurrentUser = useCallback(async (authToken, { keepUser = false } = {}) => {
    try {
      const response = await authFetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setAuthError(null);
        const data = await response.json();
        if (data.success && data.data?.user) {
          const updatedUser = data.data.user;
          if (!keepUser) {
            setUser(updatedUser);
            localStorage.setItem('auth_user', JSON.stringify(updatedUser));
          }
          // Every /me answer replaces the codes, so a role change shows without signing out.
          const codes = Array.isArray(data.data.permissions)
            ? data.data.permissions.filter((c) => typeof c === 'string')
            : [];
          setPermissions(codes);
          localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(codes));
          return updatedUser;
        }
      } else if (response.status === 429) {
        const data = await response.json().catch(() => ({}));
        const retryAfter = data.retryAfter
          ? new Date(data.retryAfter).toLocaleTimeString()
          : 'a moment';
        setAuthError(`Too many requests. Please try again after ${retryAfter}.`);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
    return null;
  }, [API_URL]);

  // ── Active location ─────────────────────────────────────────────────

  // The location the latest lookup is for: a slower, older answer never overwrites a newer one.
  const locationLookupRef = useRef(null);

  /** The active location, in state and in the two keys the boot restore reads. */
  const storeActiveLocation = useCallback((loc) => {
    setActiveLocation(loc);
    localStorage.setItem('crm_active_location', JSON.stringify(loc));
    localStorage.setItem('crm_last_location_id', String(loc.id));
  }, []);

  /**
   * Upgrade the stored location from GET /api/locations/:id: its name and its store code
   * (`locations.code`, the code the Customers table chip shows; the header shows it where the
   * name does not fit - tablet pass, item 11). `code` is null when the row has none. Never awaited:
   * a label is not worth holding up a sign-in or a location change for.
   */
  const lookupLocation = useCallback((locId, authToken) => {
    locationLookupRef.current = locId;
    authFetch(`${API_URL}/api/locations/${locId}`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then((locResponse) => locResponse.json())
      .then((locData) => {
        if (locationLookupRef.current !== locId) return;
        if (locData.success && locData.data) {
          storeActiveLocation({
            id: locData.data.id,
            name: locData.data.name,
            code: locData.data.code || null,
            color: getLocationColor(locData.data.name)
          });
        }
      })
      .catch(() => {
        // Keep what is stored.
      });
  }, [API_URL, storeActiveLocation]);

  // ── Boot: Restore Session ───────────────────────────────────────────

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('auth_user');
      const storedLocation = localStorage.getItem('crm_active_location');

      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          setPermissions(readStoredPermissions());

          // Restore location
          if (storedLocation) {
            try {
              const restored = JSON.parse(storedLocation);
              setActiveLocation(restored);
              // Stored before the header showed the store code (item 11): fetch it once.
              if (restored && restored.id && !('code' in restored)) {
                lookupLocation(restored.id, storedToken);
              }
            } catch { /* ignore */ }
          }

          setAppState('active');

          // Background refresh of user data
          fetchCurrentUser(storedToken);
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [fetchCurrentUser, lookupLocation]);

  // ── Login ───────────────────────────────────────────────────────────

  const login = useCallback(async (email, password, selectedLocationId) => {
    try {
      const response = await authFetch(`${API_URL}/api/auth/crm-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: email,
          password,
          selected_location_id: selectedLocationId || undefined
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Login failed');
      }

      const { accessToken, refreshToken, user: userData } = result.data;
      const fpc = result.data.forcePasswordChange || false;

      setToken(accessToken);
      setUser(userData);
      setForcePasswordChange(fpc);
      errorTracker.setMeta({ userId: userData.id });

      if (typeof window !== 'undefined') {
        window.__authExpired = false;
      }

      localStorage.setItem('auth_token', accessToken);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      if (refreshToken) {
        localStorage.setItem('auth_refresh_token', refreshToken);
      }

      // Resolve and store location.
      //
      // Signing in does NOT wait for this. The name and colour are the header's label;
      // they are not what makes a session valid. This lookup used to be awaited here,
      // which put a second round trip in front of every login and — when the request
      // stalled rather than failed — left the user on a disabled "Signing in..." button
      // indefinitely, because a stall never reaches a catch and the app never navigated.
      // So the fallback is applied first and the real name upgrades it when it arrives.
      const locId = userData.activeLocationId || selectedLocationId;
      if (locId) {
        storeActiveLocation({ id: locId, name: `Location ${locId}`, color: '#64748b' });
        lookupLocation(locId, accessToken);
      }

      setAppState(fpc ? 'active' : 'active'); // active either way, but force_password_change gates routes

      // The permission codes come from /me (F1). Not awaited, like the location label: the
      // sign-in is valid without them, and the gated sidebar items appear when they arrive.
      setPermissions([]);
      localStorage.removeItem(PERMISSIONS_KEY);
      fetchCurrentUser(accessToken, { keepUser: true });

      return {
        success: true,
        forcePasswordChange: fpc,
        passwordMeetsCurrentPolicy: result.data.passwordMeetsCurrentPolicy
      };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  }, [API_URL, storeActiveLocation, lookupLocation, fetchCurrentUser]);

  // ── Logout ──────────────────────────────────────────────────────────

  const logout = useCallback(() => {
    // While the token is still here: the preferences store sends any change still waiting
    // (PreferencesContext), as this user. It drops its documents when the user goes.
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('crm:before-logout'));
    const refreshToken = localStorage.getItem('auth_refresh_token');
    const accessToken = localStorage.getItem('auth_token');
    if (accessToken) {
      fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ refreshToken: refreshToken || undefined }),
      }).catch(() => {});
    }

    setUser(null);
    setToken(null);
    setActiveLocation(null);
    setForcePasswordChange(false);
    setPermissions([]);
    setAppState('unauthenticated');
    errorTracker.setMeta({ userId: null });
    localStorage.removeItem(PERMISSIONS_KEY);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_refresh_token');
    localStorage.removeItem('crm_active_location');
    if (typeof window !== 'undefined') {
      window.__authExpired = false;
    }
  }, [API_URL]);

  // ── Change Location ─────────────────────────────────────────────────

  const changeLocation = useCallback(async (newLocationId) => {
    try {
      const accessToken = localStorage.getItem('auth_token');
      const response = await authFetch(`${API_URL}/api/auth/crm-change-location`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ new_location_id: newLocationId })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to change location');
      }

      // Update token
      const newToken = result.data.accessToken;
      setToken(newToken);
      localStorage.setItem('auth_token', newToken);

      // Update location. This response has no store code; the lookup adds it (item 11).
      const loc = {
        id: result.data.location.id,
        name: result.data.location.name,
        code: result.data.location.code || null,
        color: getLocationColor(result.data.location.name)
      };
      storeActiveLocation(loc);
      lookupLocation(loc.id, newToken);

      return { success: true, location: loc };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [API_URL, storeActiveLocation, lookupLocation]);

  // ── Password Change Complete ────────────────────────────────────────

  const completePasswordChange = useCallback(() => {
    setForcePasswordChange(false);
  }, []);

  // ── Update User ─────────────────────────────────────────────────────

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('auth_user', JSON.stringify(updatedUser));
  }, []);

  // ── Sign In Again (from session expired) ────────────────────────────

  const signInAgain = useCallback(() => {
    // Keep the email for pre-fill
    const savedEmail = user?.email;
    logout();
    // Return email for pre-fill
    return savedEmail;
  }, [user, logout]);

  // ── Role Helpers ────────────────────────────────────────────────────

  const hasRole = useCallback((requiredRole) => {
    if (!user?.role) return false;
    return user.role.toLowerCase() === requiredRole.toLowerCase();
  }, [user]);

  const hasAnyRole = useCallback((roles) => {
    if (!user?.role) return false;
    const userRole = user.role.toLowerCase();
    return roles.some(role => role.toLowerCase() === userRole);
  }, [user]);

  const canApproveQuotes = useMemo(() => {
    if (!user?.role) return false;
    const approverRoles = ['admin', 'manager', 'supervisor'];
    return approverRoles.includes(user.role.toLowerCase()) || user.canApproveQuotes === true;
  }, [user]);

  const approvalThreshold = useMemo(() => {
    return user?.approvalThresholdPercent || user?.approval_threshold_percent || null;
  }, [user]);

  const isAdmin = useMemo(() => {
    return user?.role?.toLowerCase() === 'admin';
  }, [user]);

  const isManagerOrAbove = useMemo(() => {
    if (!user?.role) return false;
    const managerRoles = ['admin', 'manager', 'supervisor'];
    return managerRoles.includes(user.role.toLowerCase());
  }, [user]);

  /**
   * Whether the user holds a permission code. An admin holds every code by name, as the
   * server's requirePermission lets users.role 'admin' through (middleware/auth.js).
   */
  const hasPermission = useCallback((code) => {
    if (isAdmin) return true;
    return typeof code === 'string' && permissions.includes(code);
  }, [isAdmin, permissions]);

  /** Whether the user holds any of the codes (an empty list: no). */
  const hasAnyPermission = useCallback((codes) => (
    Array.isArray(codes) && codes.some((code) => hasPermission(code))
  ), [hasPermission]);

  const canAccessMarketplace = useMemo(() => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    const perms = user.permissions || [];
    return perms.includes('hub.marketplace.access') || perms.includes('*');
  }, [user]);

  // ── Context Value ───────────────────────────────────────────────────

  const value = useMemo(() => ({
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user && appState === 'active',
    // App state
    appState,
    forcePasswordChange,
    completePasswordChange,
    // Location
    activeLocation,
    changeLocation,
    LOCATION_COLORS,
    getLocationColor,
    // Session
    signInAgain,
    // Role helpers
    hasRole,
    hasAnyRole,
    canApproveQuotes,
    approvalThreshold,
    isAdmin,
    isManagerOrAbove,
    canAccessMarketplace,
    // Permission codes (F1)
    permissions,
    hasPermission,
    hasAnyPermission,
    // Utility
    authError,
    refreshUser: () => token && fetchCurrentUser(token)
  }), [user, token, loading, login, logout, hasRole, hasAnyRole, canApproveQuotes,
       approvalThreshold, isAdmin, isManagerOrAbove, canAccessMarketplace,
       permissions, hasPermission, hasAnyPermission,
       fetchCurrentUser, appState, forcePasswordChange, completePasswordChange,
       activeLocation, changeLocation, signInAgain, updateUser, authError]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
